export type NodeType = 'folder' | 'file';
export type FolderType = 'organisation' | 'my_drafts' | 'my_uploads' | 'group' | 'sharepoint' | 'user' | null;

export interface DocumentHubNode {
  id: string;
  name: string;
  type: NodeType;
  folder_type?: FolderType;
  read_only?: boolean;
  parent_id: string | null;
  children?: DocumentHubNode[] | null;
  mime_type?: string | null;
  size_bytes?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
  tags?: {
    group: 'document_type' | 'domain' | 'site' | 'national_returns';
    slug: string;
    label: string;
    confirmed: boolean;
  }[];
  document_type?: string | null;
  group_id?: string | null;
  document_id?: string | null;
  subtitle?: string;
  uploading?: boolean;
  created_by?: number | null;
  created_by_name?: string | null;
  personal_folder_user_id?: number | null;
  /** Populated server-side — no separate /summary fetch needed. */
  analysis_status?: 'pending' | 'extracting' | 'tagging' | 'completed' | 'failed' | null;
  has_suggestions?: boolean;
  expiry_status?: 'expired' | 'expiring_soon' | null;
  /** How this file is attached when listed in a group folder. */
  group_relation?: 'owned' | 'linked' | null;
  visibility?: 'private' | 'tenant' | 'public' | string | null;
}

export interface FolderContentsResponse {
  items: DocumentHubNode[];
  total: number;
  offset: number;
  limit: number;
}

export interface DocumentHubFilters {
  tags: string[];
  source: string[];
}

/** Source categories shown in the filter UI. */
export const SOURCE_CATEGORIES: Record<string, string[]> = {
  library: ['upload', 'wiki'],
  organisation: ['tenant_file'],
  groups: ['group_file'],
  workflows: [
    'chat_upload',
    'writing_upload',
    'written_document',
    'asset_review_upload',
    'reviewed_file',
    'action_evidence',
    'action_extraction',
    'action_update',
    'training_hub_document',
    'meeting_minutes',
    'pam_content_feedback'
  ]
};

/** Display label for each source category. */
export const SOURCE_CATEGORY_LABELS: Record<string, string> = {
  library: 'Personal Folder',
  organisation: 'Organisation Folder',
  groups: 'Group Folders',
  workflows: 'Workflows'
};

/** Chat drafts — the API rejects moving these. */
export const DRAFT_DOCUMENT_TYPE = 'canvas';

export function isDraftDocumentType(documentType: string | null | undefined): boolean {
  return documentType === DRAFT_DOCUMENT_TYPE;
}

const WORKFLOW_DOCUMENT_TYPES = new Set(SOURCE_CATEGORIES.workflows);

/** Product area that created the file — not a specific review/writing instance. */
const WORKFLOW_PRODUCT_LABELS: Record<string, string> = {
  chat_upload: 'Chat',
  writing_upload: 'Writing',
  written_document: 'Writing',
  asset_review_upload: 'Asset Review',
  reviewed_file: 'Review Assistant',
  action_evidence: 'Actions',
  action_extraction: 'Actions',
  action_update: 'Actions',
  training_hub_document: 'Training Hub',
  meeting_minutes: 'Meetings',
  pam_content_feedback: 'PAM Feedback'
};

/** Hub subfolder names under Personal Folder > Workflows. */
const WORKFLOW_HUB_FOLDER_LABELS: Record<string, string> = {
  chat_upload: 'Chat Uploads',
  writing_upload: 'Writing',
  written_document: 'Writing',
  asset_review_upload: 'Asset Review Uploads',
  reviewed_file: 'Review Uploads',
  action_evidence: 'Actions',
  action_extraction: 'Actions',
  action_update: 'Actions',
  training_hub_document: 'Training Hub',
  meeting_minutes: 'Meetings',
  pam_content_feedback: 'PAM Feedback'
};

export function isWorkflowDocumentType(documentType: string | null | undefined): boolean {
  return !!documentType && WORKFLOW_DOCUMENT_TYPES.has(documentType);
}

function joinWorkflowNames(names: string[]): string {
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

export function workflowMoveWarning(documentTypes: Array<string | null | undefined>): string {
  const names = [
    ...new Set(
      documentTypes.flatMap((documentType) => {
        if (!documentType) return [];
        const label = WORKFLOW_PRODUCT_LABELS[documentType];
        return label ? [label] : [];
      })
    )
  ];
  const product = names.length > 0 ? joinWorkflowNames(names) : 'a workflow';
  const fileCount = documentTypes.filter((documentType) => isWorkflowDocumentType(documentType)).length;
  if (fileCount <= 1) {
    return `This file came from ${product}. Moving it does not remove it from that workflow, but other people may no longer be able to open the file.`;
  }
  return `These files came from ${product}. Moving them does not remove them from those workflows, but other people may no longer be able to open the files.`;
}

/** True when the file is owned by a group (placement via ``group_resources``). */
export function isGroupOwnedNode(node: Pick<DocumentHubNode, 'group_relation'>): boolean {
  return node.group_relation === 'owned';
}

export function isOrganisationLibraryNode(node: Pick<DocumentHubNode, 'document_type' | 'visibility'>): boolean {
  const docType = node.document_type ?? '';
  return (docType === 'tenant_file' || docType === 'wiki') && node.visibility === 'tenant';
}

export function isWorkflowsFolderId(folderId: string | null | undefined): boolean {
  return folderId === 'feature-files' || !!folderId?.startsWith('feature-files/');
}

export function isDraftsFolder(node: Pick<DocumentHubNode, 'type' | 'id' | 'folder_type'>): boolean {
  return node.type === 'folder' && (node.id === 'my-drafts' || node.folder_type === 'my_drafts');
}

/** Map from underlying document_type to its display category key. */
const DOCUMENT_TYPE_TO_CATEGORY: Record<string, string> = Object.fromEntries(
  Object.entries(SOURCE_CATEGORIES).flatMap(([cat, types]) => types.map((t) => [t, cat]))
);

export function friendlySourceSegments(documentType: string | null | undefined, visibility?: string | null): string[] {
  if (!documentType) return [];
  if (documentType === DRAFT_DOCUMENT_TYPE) return ['Drafts'];
  if (documentType === 'group_file') return ['Groups'];
  if (documentType === 'wiki' && visibility === 'tenant') {
    return [SOURCE_CATEGORY_LABELS.organisation];
  }
  const cat = DOCUMENT_TYPE_TO_CATEGORY[documentType];
  if (cat === 'workflows') {
    const segments = [SOURCE_CATEGORY_LABELS.library, SOURCE_CATEGORY_LABELS.workflows];
    const subfolder = WORKFLOW_HUB_FOLDER_LABELS[documentType];
    if (subfolder) segments.push(subfolder);
    return segments;
  }
  // Unknown types fall through to Personal Folder (backend catch-all behaviour).
  return [SOURCE_CATEGORY_LABELS[cat ?? 'library'] ?? documentType];
}

export function friendlySource(documentType: string | null | undefined, visibility?: string | null): string {
  return friendlySourceSegments(documentType, visibility).join(' > ');
}
